import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Smartphone, CheckCircle2, AlertCircle, Loader2, MonitorSmartphone } from "lucide-react";
import ChangePasswordDialog from "@/components/auth/ChangePasswordDialog";
import TwoFactorAuthDialog from "@/components/auth/TwoFactorAuthDialog";
import SessionManager from "@/components/security/SessionManager";
import TrustedDevicesManager from "@/components/security/TrustedDevicesManager";
import { twoFactorService } from "@/services/twoFactorService";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const SecuritySection = () => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [twoFactorSettings, setTwoFactorSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    loadTwoFactorSettings();
  }, []);

  const loadTwoFactorSettings = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        setUserId(user.id);
        const settings = await twoFactorService.getSettings(user.id);
        setTwoFactorSettings(settings);
      }
    } catch (error) {
      console.error('Error loading 2FA settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestSMS = async () => {
    if (!userId) return;
    
    try {
      setIsTesting(true);
      await twoFactorService.sendSMSCode(userId);
      
      toast({
        title: "Test SMS sent",
        description: "Check your phone for the verification code. This is just a test, you don't need to enter it.",
      });
    } catch (error: any) {
      toast({
        title: "Failed to send SMS",
        description: error.message || "Could not send test SMS. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!userId || !confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }
    
    try {
      await twoFactorService.disable(userId);
      setTwoFactorSettings(null);
      
      toast({
        title: "2FA disabled",
        description: "Two-factor authentication has been disabled for your account.",
      });
    } catch (error: any) {
      toast({
        title: "Failed to disable 2FA",
        description: error.message || "Could not disable 2FA. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handle2FADialogClose = (changed: boolean) => {
    setShow2FADialog(false);
    if (changed) {
      loadTwoFactorSettings();
    }
  };

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">Security</h2>
          <Separator className="mb-6" />
          
          <div className="space-y-6">
            {/* Change Password */}
            <div>
              <h3 className="text-lg font-medium mb-2">Change Password</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Update your password to keep your account secure.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordDialog(true)}
              >
                Change Password
              </Button>
            </div>
            
            <Separator />
            
            {/* Two-Factor Authentication */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium">Two-Factor Authentication</h3>
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : twoFactorSettings?.enabled ? (
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Not Enabled
                  </Badge>
                )}
              </div>

              {!isLoading && twoFactorSettings?.enabled ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    {twoFactorSettings.method === 'sms' ? (
                      <Smartphone className="h-5 w-5 text-primary mt-0.5" />
                    ) : (
                      <Shield className="h-5 w-5 text-primary mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">
                        {twoFactorSettings.method === 'sms' ? 'SMS Authentication' : 'Authenticator App (TOTP)'}
                      </p>
                      {twoFactorSettings.method === 'sms' && twoFactorSettings.phone_number && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {twoFactorService.maskPhoneNumber(twoFactorSettings.phone_number)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {twoFactorSettings.method === 'sms' 
                          ? 'You receive verification codes via SMS text message.'
                          : 'You use an authenticator app to generate verification codes.'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {twoFactorSettings.method === 'sms' && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleTestSMS}
                        disabled={isTesting}
                      >
                        {isTesting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Test SMS
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => setShow2FADialog(true)}
                    >
                      Change Method
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleDisable2FA}
                      className="text-destructive hover:text-destructive"
                    >
                      Disable 2FA
                    </Button>
                  </div>

                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Keep your recovery codes safe. You'll need them if you lose access to your {twoFactorSettings.method === 'sms' ? 'phone' : 'authenticator app'}.
                    </AlertDescription>
                  </Alert>

                  {/* Trusted Devices Section - Only shown when 2FA is enabled */}
                  <Separator className="my-4" />
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MonitorSmartphone className="h-5 w-5 text-muted-foreground" />
                      <h4 className="font-medium">Trusted Devices</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Devices that can skip 2FA verification for 30 days. Remove devices you no longer use.
                    </p>
                    {userId && <TrustedDevicesManager userId={userId} />}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Add an extra layer of security to your account by requiring a verification code in addition to your password.
                  </p>
                  <Button 
                    variant="default"
                    onClick={() => setShow2FADialog(true)}
                    disabled={isLoading}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Set Up 2FA
                  </Button>
                </div>
              )}
            </div>
            
            <Separator />
            
            {/* Session Management */}
            <div>
              <h3 className="text-lg font-medium mb-4">Session Management</h3>
              <SessionManager />
            </div>
          </div>
        </CardContent>
      </Card>

      <ChangePasswordDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
      />

      <TwoFactorAuthDialog
        open={show2FADialog}
        onOpenChange={handle2FADialogClose}
        onSettingsChange={loadTwoFactorSettings}
      />
    </>
  );
};

export default SecuritySection;
